/**
 * PATH: packages/email-hub/src/feature/reader-header/workflow/workflowModel.ts
 * FILE_ID: EMAILHUB-READERHEADER-WORKFLOW-MODEL-001
 * PURPOSE: Pure derivations for the workflow row — owner status (incl.
 *   departed), optimistic assignment/watcher predictions, invite-candidate
 *   exclusion, the 2-avatar watcher cap, and clipboard text builders.
 * ROLE IN ARCHITECTURE: Pure I→O layer under useWorkflowCells. No React.
 * MAIN EXPORTS: deriveOwnerStatus, deriveAssigneeMember, deriveInviteCandidates,
 *   predictAssignment, predictWatcher, splitWatchersForDisplay,
 *   buildWatcherListText, WATCHER_AVATAR_CAP.
 * NON-RESPONSIBILITIES: Mutations (useWorkflowCells), rendering (WorkflowRow),
 *   bucket metadata (email-allocation helpers own BUCKET_META).
 * NOTES FOR FUTURE AI: deriveAssigneeMember/deriveInviteCandidates mirror the
 *   memo logic inside email-allocation useAssignment/useWatchers — if the
 *   exclusion rule changes there (assignee never doubles as watcher), change
 *   it here in the same MR.
 */
import type {
  AllocationMember,
} from '../../components/email-allocation'
import type { Email, EmailAssignment, EmailWatcher } from '../../hooks/useEmails'

/** At-rest watcher avatars shown in the cell before collapsing to "+N". */
export const WATCHER_AVATAR_CAP = 2

export type OwnerStatus = 'none' | 'active' | 'departed'

/**
 * Classify the owner cell. 'departed' = an assignee id exists but the host's
 * member roster (non-empty) no longer contains it — the plan requires the
 * label instead of a silently broken avatar. An EMPTY roster cannot prove
 * departure, so it resolves 'active' (fail quiet, never falsely label).
 */
export function deriveOwnerStatus(
  assignment: EmailAssignment | null | undefined,
  members: readonly AllocationMember[],
): OwnerStatus {
  const assigneeId = assignment?.assignee_user_id ?? null
  if (assigneeId == null) return 'none'
  if (members.length === 0) return 'active'
  return members.some((member) => member.id === assigneeId)
    ? 'active'
    : 'departed'
}

/** Mirror of useAssignment's assigneeMember memo (pure form). */
export function deriveAssigneeMember(
  members: readonly AllocationMember[],
  assignment: EmailAssignment | null | undefined,
): AllocationMember | null {
  const assigneeId = assignment?.assignee_user_id ?? null
  if (assigneeId == null) return null
  return members.find((member) => member.id === assigneeId) ?? null
}

/**
 * Mirror of useWatchers' inviteCandidates memo: exclude teammates already
 * watching AND the current assignee (the assignee is the primary recipient —
 * doubling them as a watcher is noise, plan US-23).
 */
export function deriveInviteCandidates(
  members: readonly AllocationMember[],
  watchers: readonly EmailWatcher[],
  assigneeUserId: number | null,
): AllocationMember[] {
  const watching = new Set(watchers.map((watcher) => watcher.user_id))
  return members.filter(
    (member) => !watching.has(member.id) && member.id !== assigneeUserId,
  )
}

/**
 * Optimistic assignment prediction painted before the server confirms.
 * The server payload REPLACES this on success; failure rolls back to prior.
 * Placeholder ids/timestamps are deliberate — never persisted.
 */
export function predictAssignment(
  prior: EmailAssignment | null,
  member: AllocationMember | null,
  patch: Partial<Pick<EmailAssignment, 'bucket' | 'scheduled_for'>> = {},
): EmailAssignment | null {
  if (member === null && patch.bucket === undefined && patch.scheduled_for === undefined) {
    return null
  }
  const base: EmailAssignment = prior ?? {
    id: -1,
    assignee_user_id: null,
    assignee_email: '',
    assignee_name: '',
    bucket: 'no_date',
    scheduled_for: null,
    notes: '',
    assigned_by_id: null,
    assigned_at: '',
    updated_at: '',
  }
  return {
    ...base,
    ...(member !== null
      ? {
          assignee_user_id: member.id,
          assignee_name: member.name,
          assignee_email: member.email ?? '',
        }
      : {}),
    ...patch,
  }
}

/** Optimistic watcher row painted while the invite POST is in flight. */
export function predictWatcher(member: AllocationMember): EmailWatcher {
  return {
    id: -1,
    user_id: member.id,
    user_email: member.email ?? '',
    user_name: member.name,
    role: 'replier',
    invited_by_id: null,
    invited_at: '',
    note: '',
  }
}

export interface WatcherDisplaySplit {
  visible: EmailWatcher[]
  overflowCount: number
}

/** Cell cap: up to two avatars, the rest collapse into "+N" (plan US-23). */
export function splitWatchersForDisplay(
  watchers: readonly EmailWatcher[],
  cap: number = WATCHER_AVATAR_CAP,
): WatcherDisplaySplit {
  return {
    visible: watchers.slice(0, cap),
    overflowCount: Math.max(0, watchers.length - cap),
  }
}

/** Clipboard text for "Copy watcher list": one `Name <email>` line each. */
export function buildWatcherListText(
  watchers: readonly EmailWatcher[],
): string {
  return watchers
    .map((watcher) =>
      watcher.user_email
        ? `${watcher.user_name || watcher.user_email} <${watcher.user_email}>`
        : watcher.user_name,
    )
    .filter(Boolean)
    .join('\n')
}

/** True when the email payload can drive the workflow row at all. */
export function workflowRowEnabled(
  email: Pick<Email, 'id'>,
  members: readonly AllocationMember[] | undefined,
): boolean {
  return Boolean(email.id) && members !== undefined
}
